/// <amd-module name="VSS/Features/Search/SearchHelp/SearchHelp" />
import React = require("react");
import { announce } from "VSS/Core/Util/Accessibility";
import { format } from "VSS/Core/Util/String";
import SearchResources = require("VSS/Features/Search/Resources");
import { FormatComponent } from "VSS/Platform/Components/Format";
import { ISearchInput } from "VSS/Platform/Search";
import { Icon, IconSize } from "VSSUI/Icon";
import { ISearchFilter, ISearchFilterGroup, ISearchHelpComponentProps } from "./SearchHelp.Props";

/**
 * This implements a search help UI that interacts with the ISearchInputComponent.
 * It allows the caller to show custom help based on the current provider being
 * searched.
 */
export class SearchHelpComponent extends React.Component<ISearchHelpComponentProps, {}> {
    private helpElement: HTMLDivElement | null;

    public render(): JSX.Element {
        return (
            <div
                ref={helpElement => {
                    this.helpElement = helpElement;
                }}
                className="search-help body-xs"
                onKeyDown={this.onKeyDown}
                onMouseDown={this.onMouseDown}
            >
                {this.props.filterGroups.map((filterGroup, index) => {
                    return (
                        <SearchFilterGroupComponent
                            key={index}
                            filterGroup={filterGroup}
                            searchInput={this.props.searchInput}
                            filterText={this.props.filterText}
                            onItemActivated={this.props.onItemActivated}
                            onRenderFilterText={this.props.onRenderFilterText}
                        />
                    );
                })}
                {this.props.helpLink ? (
                    <div>
                        <FormatComponent format={SearchResources.SearchFindHelp}>
                            <a className="search-link" href={this.props.helpLink} tabIndex={-1} target="vss-search-help">
                                {SearchResources.SearchHelpPage}
                            </a>
                        </FormatComponent>
                    </div>
                ) : null}
            </div>
        );
    }

    public focus(): void {
        const firstElement = this.helpElement !== null ? (this.helpElement.querySelector("[tabIndex='-1']") as HTMLElement | null) : null;
        if (firstElement) {
            firstElement.focus();
        }
    }

    private onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.which === 38 /* KeyCode.UpArrow */ || event.which === 40 /* KeyCode.DownArrow */) {
            if (this.helpElement !== null) {
                const focusedElement = document.activeElement;
                const allElements = this.helpElement.querySelectorAll("[tabIndex='-1']");
                let currentIndex = 0;

                // Figure out which element is currently selected.
                for (currentIndex = 0; currentIndex < allElements.length; currentIndex++) {
                    if (focusedElement === allElements[currentIndex]) {
                        break;
                    }
                }

                // If some undetermined element has focus we will start from the beginnging.
                if (currentIndex === allElements.length) {
                    currentIndex = -1;
                }

                // Compute the element that should retrieve focus.
                if (event.which === 38 /* KeyCode.UpArrow */) {
                    if (--currentIndex < 0) {
                        currentIndex = allElements.length - 1;
                    }
                } else {
                    if (++currentIndex === allElements.length) {
                        currentIndex = 0;
                    }
                }

                // Set focus to the next element in the sequence.
                if (currentIndex >= 0 && currentIndex < allElements.length) {
                    (allElements[currentIndex] as HTMLElement).focus();
                }

                // We dont want any default behavior, like scrolling the cointainer.
                event.preventDefault();
            }
        }
    };

    private onMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
        event.preventDefault();
    };
}

interface SearchFilterGroupComponentProps {
    filterGroup: ISearchFilterGroup;
    searchInput: ISearchInput;
    filterText?: string;
    onItemActivated?: (item: ISearchFilter) => void;
    onRenderFilterText?: (filter: ISearchFilter, filterText: string) => JSX.Element | string;
}

interface SearchFilterGroupComponentState {
    expanded: boolean;
    visibleCount: number;
}

class SearchFilterGroupComponent extends React.Component<SearchFilterGroupComponentProps, SearchFilterGroupComponentState> {
    constructor(props: SearchFilterGroupComponentProps) {
        super(props);

        // Initialize the state to show 10 filters be default.
        this.state = { expanded: false, visibleCount: 10 };
    }

    public render(): JSX.Element {
        return (
            <div>
                <div>
                    {this.props.filterGroup.caption ? <span className="search-filter-caption">{this.props.filterGroup.caption}</span> : null}
                    {this.props.filterGroup.example ? <span className="search-filter-example">{this.props.filterGroup.example}</span> : null}
                </div>
                <ul 
                    role={"listbox"} 
                    className={"search-filter-list " + (this.props.filterGroup.singleLine ? "compact" : "detailed")} 
                    aria-label={SearchResources.SearchFiltersListAriaLabel}
                >
                    {this.props.filterGroup.filters.map((filter, index) => {
                        if (index >= this.state.visibleCount) {
                            return null;
                        }
                        return (
                            <SearchFilterComponent
                                key={index}
                                filter={filter}
                                searchInput={this.props.searchInput}
                                filterText={this.props.filterText}
                                onItemActivated={this.props.onItemActivated}
                                onRenderFilterText={this.props.onRenderFilterText}
                            />
                        );
                    })}
                    {this.props.filterGroup.filters.length >= 10 ? (
                        <li
                            className="search-filter-item search-filter-expander"
                            onClick={this.toggleExpander}
                            aria-expanded={this.state.expanded}
                            role={"button"}
                            onKeyDown={this.onKeyDown}
                            tabIndex={-1}
                            aria-label={this.state.expanded ? SearchResources.SearchHelpLess : SearchResources.SearchHelpMore}
                        >
                            <Icon
                                className="search-filter-expander-icon"
                                iconName={this.state.expanded ? "ChevronUpMed" : "ChevronDownMed"}
                                size={IconSize.small}
                            />
                            <span>{this.state.expanded ? SearchResources.SearchHelpLess : SearchResources.SearchHelpMore}</span>
                        </li>
                    ) : null}
                    {this.props.filterGroup.footer ? (
                        <li className="search-filter-item search-filter-footer">{this.props.filterGroup.footer}</li>
                    ) : null}
                    {this.props.filterGroup.filters.length === 0 ? <li className="help-no-suggestion">{SearchResources.HelpNoSuggestions}</li> : null}
                </ul>
            </div>
        );
    }

    private onKeyDown = (event: React.KeyboardEvent<HTMLLIElement>) => {
        if (event.which === 13 /* KeyCode.Return */) {
            this.toggleExpander();
        }
    };

    private toggleExpander = () => {
        const count = this.state.expanded ? 10 : Number.MAX_SAFE_INTEGER;
        this.setState({ expanded: !this.state.expanded, visibleCount: count });
        const filtersCount = Math.min(count, this.props.filterGroup.filters.length);
        announce(format(SearchResources.ShowingNItems, filtersCount));
    };
}

interface SearchFilterComponentProps {
    filter: ISearchFilter;
    searchInput: ISearchInput;
    filterText?: string;
    onItemActivated?: (item: ISearchFilter) => void;
    onRenderFilterText?: (filter: ISearchFilter, filterText: string | undefined) => JSX.Element | string;
}

class SearchFilterComponent extends React.Component<SearchFilterComponentProps, {}> {
    private listItem: HTMLLIElement | null;

    public render(): JSX.Element {
        const { onRenderFilterText, filter, filterText } = this.props;
        return (
            <li
                ref={listItem => {
                    this.listItem = listItem;
                }}
                role={"option"}
                className="search-filter-item"
                onClick={this.onActivate}
                onKeyDown={this.onKeyDown}
                tabIndex={-1}
            >
                <div className="search-filter-text">{onRenderFilterText ? onRenderFilterText(filter, filterText) : filter.text}</div>
                {this.props.filter.hint ? <div className="search-filter-hint">{this.props.filter.hint}</div> : null}
            </li>
        );
    }

    private onActivate = () => {
        // IE11: Doesnt focus the item when you click on the child text. We will force
        //  focus onto the list-item when it is activated.
        if (this.listItem != null) {
            this.listItem.focus();
        }

        const { filter, searchInput, onItemActivated } = this.props;
        const queryText = this.props.searchInput.queryText();
        const filterString = filter.shortcutText || filter.text;

        // Get suggested text upon filter activation.
        const suggestedString = filter.getSuggestedText ? filter.getSuggestedText(queryText, filterString) : filterString;

        // Replace the current input in search input if needed.
        filter.replaceTextOnActivation ? searchInput.replaceText(suggestedString) : searchInput.appendText(suggestedString);

        if (onItemActivated) {
            onItemActivated(filter);
        }
    };

    private onKeyDown = (event: React.KeyboardEvent<HTMLLIElement>) => {
        if (event.which === 13 /* KeyCode.Return */) {
            this.onActivate();
        }
    };
}
